import { useEffect, useState } from 'react';
import { Modal, ModalFooter, Button, Select } from '@tkpod/ui';
import {
  DesignContext,
  suggestedDuplicateDesignContext,
  type UptDesignContext,
} from '@tkpod/shared';

const CONTEXT_OPTIONS: Array<{ value: UptDesignContext; label: string }> = [
  { value: DesignContext.PRODUCT, label: 'Product (My products / POD)' },
  { value: DesignContext.STOREFRONT, label: 'Storefront (custom products)' },
];

function contextLabel(value: string): string {
  if (value === DesignContext.STOREFRONT) return 'Storefront';
  if (value === DesignContext.PRODUCT) return 'Product';
  return value;
}

function copyHint(source: string, target: UptDesignContext): string {
  if (source === DesignContext.STOREFRONT && target === DesignContext.PRODUCT) {
    return 'Storefront catalogs become Product copies by default so they can be used in My products.';
  }
  if (source === target) {
    return `The copy will stay ${contextLabel(target)} — same designer context as this catalog.`;
  }
  return `The copy will be created as ${contextLabel(target)}. Designer context cannot be changed after create.`;
}

interface DuplicateTemplateModalProps {
  open: boolean;
  sourceName: string;
  sourceDesignContext: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: (designContext: UptDesignContext) => void;
}

export function DuplicateTemplateModal({
  open,
  sourceName,
  sourceDesignContext,
  loading,
  onClose,
  onConfirm,
}: DuplicateTemplateModalProps) {
  const [designContext, setDesignContext] = useState<UptDesignContext>(
    suggestedDuplicateDesignContext(sourceDesignContext),
  );

  useEffect(() => {
    if (open) {
      setDesignContext(suggestedDuplicateDesignContext(sourceDesignContext));
    }
  }, [open, sourceDesignContext]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Duplicate template"
      description={`Creates a DRAFT copy of "${sourceName}" (options, variants, print areas, pricing). Provider SKUs and Shopify mappings are not copied — re-map before setting ACTIVE.`}
    >
      <div className="space-y-3 p-4">
        <p className="text-sm text-text-secondary">
          This catalog:{' '}
          <span className="font-medium text-text-primary">
            {contextLabel(sourceDesignContext)}
          </span>
        </p>
        <Select
          label="Designer context for the copy"
          value={designContext}
          onChange={(e) =>
            setDesignContext(e.target.value as UptDesignContext)
          }
          options={CONTEXT_OPTIONS}
          hint={copyHint(sourceDesignContext, designContext)}
        />
      </div>
      <ModalFooter>
        <Button variant="outline" size="sm" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          size="sm"
          loading={loading}
          onClick={() => onConfirm(designContext)}
        >
          Duplicate as {contextLabel(designContext)}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
